import React from 'react';
import { EditableColumnType } from 'components/CustomTable/CustomTable';
import { PlusOutlined } from '@ant-design/icons';
import AdditionalInformation from 'components/AdditionalInformation/AdditionalInformation';
import { DataType } from 'store/ducks/DataEntry/Branch/types';
import CustomSelect from 'components/CustomSelect';
import { DatePicker, Button } from 'antd';
import { IEntityTitle } from 'store/ducks/DataEntry/Entities/types';
import dayjs from 'dayjs';
import * as Styled from '../Tables/DataEntry/DataEntry.styled';

export const getEntityColumns = (
  handleSave: (record: IEntityTitle) => void,
  handleRemoveRow: (key: number) => void,
  handleInactivate: (record: IEntityTitle) => void,
  handleAddRow: (record: { key: React.Key }, index: number) => void,
  countries: { value: string; label: string }[],
  entityTypes: { value: string; label: string }[],
  hierarchyInGroups: { value: string; label: string }[],
  competentAuthorities: { value: string; label: string }[],
  currencies: { value: string; label: string }[],
  natures: { value: string; label: string }[],
  entityMaintaining: {
    financial_entity_name: string;
    id: number;
    key: number;
  }[],
  t: any,
): EditableColumnType[] => [
  {
    title: (
      <>
        {t('FINANCIAL_ENTITY.ENTITY_NAME')}
        <Styled.Asterisx>*</Styled.Asterisx>
      </>
    ),
    dataIndex: 'name_of_entity',
    editable: true,
    fixed: 'left',
    className: 'highlighted-column',
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p> {t('FINANCIAL_ENTITY.DATE_OF_THE_REPORTING')}</p>
          <p>B_01.01.0060</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('FINANCIAL_ENTITY.DATE_OF_THE_REPORTING_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'reporting_date',
    editable: false,
    className: 'highlighted-column',
    render: (text: string, record: DataType) => {
      const entityRecord = record as IEntityTitle;
      return (
        <DatePicker
          value={text ? dayjs(text) : null}
          format="YYYY-MM-DD"
          onClick={(e) => e.stopPropagation()}
          onChange={(date) => {
            const formattedDateString = date
              ? dayjs(date).format('YYYY-MM-DD')
              : '';
            const updatedRow: IEntityTitle = {
              ...entityRecord,
              reporting_date: formattedDateString,
            };
            handleSave(updatedRow);
          }}
        />
      );
    },
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p> {t('FINANCIAL_ENTITY.TYPE_OF_FINANCIAL_ENTITY')} </p>
          <p>B_01.02.0040</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('FINANCIAL_ENTITY.TYPE_OF_FINANCIAL_ENTITY_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'entity_type_id',
    editable: false,
    className: 'highlighted-column',
    render: (_, record) => {
      return (
        <CustomSelect
          options={entityTypes}
          value={record.entity_type_id}
          onChange={(value: any) => {
            handleSave({
              ...record,
              entity_type_id: value,
            } as IEntityTitle);
          }}
          filterOption={(input: string, option: { label: string }) =>
            option?.label?.toLowerCase().includes(input.toLowerCase())
          }
        />
      );
    },
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('FINANCIAL_ENTITY.ENTITY_MAINTAINING')} </p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
      </Styled.TitleComponent>
    ),
    dataIndex: 'financial_entity_lei_id',
    editable: false,
    className: 'highlighted-column',
    render: (_, record) => (
      <CustomSelect
        options={entityMaintaining.map(
          (option: {
            id: number;
            key: any;
            financial_entity_name: string;
          }) => ({
            value: option.id,
            key: option.key,
            label: option.financial_entity_name,
          }),
        )}
        value={record.financial_entity_lei_id}
        onChange={(value: any) =>
          handleSave({
            ...record,
            financial_entity_lei_id: value,
          } as IEntityTitle)
        }
        filterOption={(input: string, option: { label: string }) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
        disabled={!record.isNew}
      />
    ),
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('FINANCIAL_ENTITY.LEI_OF_FINANCIAL_ENTITY')} </p>
          <p>B_01.02.0010</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('FINANCIAL_ENTITY.LEI_OF_FINANCIAL_ENTITY_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'lei_of_entity',
    editable: true,
    className: 'highlighted-column',
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('FINANCIAL_ENTITY.LEI_OF_DIRECT_PARENT_UNDERTAKING')}</p>
          <p>B_01.02.0060</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t(
            'FINANCIAL_ENTITY.LEI_OF_DIRECT_PARENT_UNDERTAKING_INFO',
          )}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'parent_lei',
    editable: true,
    className: 'highlighted-column',
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('FINANCIAL_ENTITY.LEI_OF_FINANCIAL_ENTITY_MAKING_USE')}</p>
          <p>B_02.02.0020</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t(
            'FINANCIAL_ENTITY.LEI_OF_FINANCIAL_ENTITY_MAKING_USE_INFO',
          )}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'lei_entity_making_use_ICT_service',
    editable: true,
    className: 'highlighted-column',
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('FINANCIAL_ENTITY.LEI_OF_ENTITY_SIGNING')}</p>
          <p>B_02.0020</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('FINANCIAL_ENTITY.LEI_OF_ENTITY_SIGNING_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'lei_entity_signing_contractual_arrangement',
    editable: true,
    className: 'highlighted-column',
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('FINANCIAL_ENTITY.DATE_OF_INTEGRATION')}</p>
          <p>B_01.02.0080</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('FINANCIAL_ENTITY.DATE_OF_INTEGRATION_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'integration_date',
    editable: false,
    className: 'highlighted-column',
    render: (text: string, record: DataType) => {
      const entityRecord = record as IEntityTitle;
      return (
        <Styled.RelativeDateWrapper>
          <DatePicker
            value={text ? dayjs(text) : null}
            format="YYYY-MM-DD"
            onClick={(e) => e.stopPropagation()}
            onChange={(date) => {
              const formattedDateString = date
                ? dayjs(date).format('YYYY-MM-DD')
                : '';
              const updatedRow: IEntityTitle = {
                ...entityRecord,
                integration_date: formattedDateString,
              };
              handleSave(updatedRow);
            }}
          />
        </Styled.RelativeDateWrapper>
      );
    },
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('FINANCIAL_ENTITY.DATE_OF_DELETION')}</p>
          <p>B_01.02.0090</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('FINANCIAL_ENTITY.DATE_OF_DELETION_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'deletion_date',
    editable: false,
    className: 'highlighted-column',
    render: (text: string, record: DataType) => {
      const entityRecord = record as IEntityTitle;
      return (
        <DatePicker
          value={text ? dayjs(text) : dayjs('9999-01-01')}
          format="YYYY-MM-DD"
          onClick={(e) => e.stopPropagation()}
          onChange={(date) => {
            const formattedDateString = date
              ? dayjs(date).format('YYYY-MM-DD')
              : '';
            const updatedRow: IEntityTitle = {
              ...entityRecord,
              deletion_date: formattedDateString,
            };
            handleSave(updatedRow);
          }}
        />
      );
    },
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('FINANCIAL_ENTITY.VALUE_OF_TOTAL_ASSETS')}</p>
          <p> B_01.02.0110 </p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('FINANCIAL_ENTITY.VALUE_OF_TOTAL_ASSETS_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'total_assets',
    editable: true,
    className: 'highlighted-column',
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('FINANCIAL_ENTITY.DATE_OF_LAST_UPDATE')}</p>
          <p>B_01.02.0070</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('FINANCIAL_ENTITY.DATE_OF_LAST_UPDATE_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'date_of_last_update',
    editable: false,
    className: 'highlighted-column',
    render: (text: string, record: DataType) => {
      const entityRecord = record as IEntityTitle;
      return (
        <DatePicker
          value={text ? dayjs(text) : null}
          format="YYYY-MM-DD"
          onClick={(e) => e.stopPropagation()}
          onChange={(date) => {
            const formattedDateString = date
              ? dayjs(date).format('YYYY-MM-DD')
              : '';
            const updatedRow: IEntityTitle = {
              ...entityRecord,
              date_of_last_update: formattedDateString,
            };
            handleSave(updatedRow);
          }}
        />
      );
    },
  },
  {
    title: (
      <>
        {t('FINANCIAL_ENTITY.ENTITY_VALIDITY_FROM_TO')}
        <Styled.Asterisx>*</Styled.Asterisx>
      </>
    ),
    dataIndex: 'date_range',
    editable: false,
    render: (_: any, record: DataType) => {
      const entityRecord = record as IEntityTitle;

      return (
        <Styled.DateInputs>
          <DatePicker
            value={
              entityRecord.start_date ? dayjs(entityRecord.start_date) : null
            }
            format="YYYY-MM-DD"
            onClick={(e) => e.stopPropagation()}
            onChange={(date) => {
              const formattedDateString = date
                ? dayjs(date).format('YYYY-MM-DD')
                : '';

              const updatedRow: IEntityTitle = {
                ...entityRecord,
                start_date: formattedDateString,
              };

              handleSave(updatedRow);
            }}
          />

          <DatePicker
            value={
              entityRecord.end_date
                ? dayjs(entityRecord.end_date)
                : dayjs('9999-01-01')
            }
            format="YYYY-MM-DD"
            onClick={(e) => e.stopPropagation()}
            onChange={(date) => {
              const formattedDateString = date
                ? dayjs(date).format('YYYY-MM-DD')
                : '';

              const updatedRow: IEntityTitle = {
                ...entityRecord,
                end_date: formattedDateString,
              };

              handleSave(updatedRow);
            }}
          />
        </Styled.DateInputs>
      );
    },
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('FINANCIAL_ENTITY.HIERARCHY_OF_ENTITY')}</p>
          <p>B_01.02.0050</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('FINANCIAL_ENTITY.HIERARCHY_OF_ENTITY_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'hierarchy_in_group_id',
    editable: false,
    className: 'highlighted-column',
    render: (_, record) => (
      <CustomSelect
        options={hierarchyInGroups}
        value={record.hierarchy_in_group_id}
        onChange={(value: any) =>
          handleSave({
            ...record,
            hierarchy_in_group_id: value,
          } as IEntityTitle)
        }
        filterOption={(input: string, option: { label: string }) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('FINANCIAL_ENTITY.NATURE_OF_FINANCIAL_ENTITY')}</p>
          <p> B_04.01.0030</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('FINANCIAL_ENTITY.NATURE_OF_FINANCIAL_ENTITY_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'nature_id',
    editable: false,
    className: 'highlighted-column',
    render: (_, record) => (
      <CustomSelect
        options={natures}
        value={record.nature_id}
        onChange={(value: any) =>
          handleSave({
            ...record,
            nature_id: value,
          } as IEntityTitle)
        }
        filterOption={(input: string, option: { label: string }) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('FINANCIAL_ENTITY.COMPETENT_AUTHORITY')}</p>
          <p>B_01.01.0050</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('FINANCIAL_ENTITY.COMPETENT_AUTHORITY_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'competent_authority_id',
    editable: false,
    className: 'highlighted-column',
    render: (_, record) => (
      <CustomSelect
        options={competentAuthorities}
        value={record.competent_authority_id}
        onChange={(value: any) =>
          handleSave({
            ...record,
            competent_authority_id: value,
          } as IEntityTitle)
        }
        filterOption={(input: string, option: { label: string }) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('FINANCIAL_ENTITY.CURRENCY')}</p>
          <p>B_01.02.0100</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('FINANCIAL_ENTITY.CURRENCY_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'currency_id',
    editable: false,
    className: 'highlighted-column',
    render: (_, record) => (
      <CustomSelect
        options={currencies}
        value={record.currency_id}
        onChange={(value: any) =>
          handleSave({
            ...record,
            currency_id: value,
          } as IEntityTitle)
        }
        filterOption={(input: string, option: { label: string }) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('FINANCIAL_ENTITY.COUNTRY_OF_ENTITY')}</p>
          <p>B_01.02.0030</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('FINANCIAL_ENTITY.COUNTRY_OF_ENTITY_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'country_id',
    editable: false,
    className: 'highlighted-column',
    render: (_, record) => (
      <CustomSelect
        options={countries}
        value={record.country_id}
        onChange={(value: any) =>
          handleSave({
            ...record,
            country_id: value,
          } as IEntityTitle)
        }
        filterOption={(input: string, option: { label: string }) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  },
  {
    title: t('FINANCIAL_ENTITY.DESCRIPTION'),
    dataIndex: 'description',
    editable: true,
  },
  {
    title: t('GENERAL.ACTIONS'),
    key: 'actions',
    render: (_: any, record) => (
      <>
        {record.isNew ? (
          <Button danger onClick={() => handleRemoveRow(record.key)}>
            {t('GENERAL.REMOVE')}
          </Button>
        ) : (
          <Button
            danger
            onClick={() => handleInactivate(record as IEntityTitle)}
          >
            {t('GENERAL.SOFT_DELETE')}
          </Button>
        )}
      </>
    ),
  },
];
